import type { AgentClient } from '@estudio/contracts';

/** Experimental personal NotebookLM UI adapter. No Google session data crosses IPC. */
export interface NotebookStatus {
  browserInstalled:boolean;
  state:'disconnected'|'login_open'|'session_pending'|'ready'|'busy'|'error';
  message:string;
}
export interface NotebookUploadResult {
  items:Array<{title:string;projectId?:string;revisionId?:string|null;state:'confirmed'|'skipped'|'ambiguous'|'failed';sourceId?:string}>;
  message:string;
}
/** Personal UI observations, not stable Google API identifiers. */
export interface NotebookSourceRef {title:string;marker?:string;expectedSha256?:string}
export interface NotebookSourceList {items:NotebookSourceRef[];message:string}
export interface NotebookSourceRead {sourceRef:NotebookSourceRef;title:string;text:string;sha256:string}
export interface NotebookSyncResult {
  items:Array<{documentId:string;revisionId?:string;state:'confirmed'|'skipped'|'ambiguous'|'conflict'|'failed';sourceRef?:NotebookSourceRef}>;
  message:string;
}
export interface NotebookSyncInput {
  channelId:string;documentIds:string[];operationId:string;direction?:'export'|'import';
  /** Import is an explicit action after an original-source preview. */
  bindingId?:string;expectedRemoteSha256?:string;
}

/**
 * AuthPort: guardar/renovar sesión y completar el acceso.
 * Web: sesión del cliente Supabase en el navegador. Nativo (B): almacenamiento
 * seguro del sistema; OAuth/deep link con PKCE y lista exacta de redirecciones.
 * Nunca se envía un refresh token en una URL.
 */
export interface AuthSession {
  userId: string;
  email: string | null;
  accessToken: string;
  expiresAt: number | null;
}

export interface AuthPort {
  getSession(): Promise<AuthSession | null>;
  /** Envía un código de un solo uso al correo (sin crear usuarios: registro cerrado). */
  requestEmailCode(email: string): Promise<void>;
  verifyEmailCode(email: string, code: string): Promise<AuthSession>;
  signOut(): Promise<void>;
  onChange(listener: (session: AuthSession | null) => void): () => void;
}

/**
 * SecureSessionStorage: dónde guarda la sesión el cliente de autenticación.
 * Web: localStorage del navegador (por defecto). Nativo (B): almacén seguro del
 * sistema (Keychain, Credential Manager, Secret Service). Compatible con el
 * `auth.storage` de supabase-js.
 */
export interface SecureSessionStorage {
  getItem(key: string): Promise<string | null> | string | null;
  setItem(key: string, value: string): Promise<void> | void;
  removeItem(key: string): Promise<void> | void;
}

/**
 * Lo que el ejecutable (B) inyecta en `window.__ESTUDIO_DESKTOP__` antes de cargar
 * la interfaz. Todo es opcional: lo que falte usa el adaptador web.
 */
export interface DesktopInjection {
  updates?: {
    status(): Promise<DesktopUpdateState>;
    check(): Promise<DesktopUpdateState>;
    download(): Promise<DesktopUpdateState>;
    install(): Promise<void>;
  };
  bridge?: import('./bridge.ts').DesktopBridge;
  sessionStorage?: SecureSessionStorage;
  drafts?: DraftStore;
  emailLink?: {
    prepare(): Promise<string>;
    subscribe(listener: (code: string) => void): () => void;
  };
  /** Optional v1 extension: native workspace binding before mounting local features. */
  host?: {
    notebookStatus?():Promise<NotebookStatus>;
    notebookConnect?():Promise<{started:boolean;message:string}>;
    notebookVerify?():Promise<NotebookStatus>;
    notebookDisconnect?():Promise<{disconnected:boolean}>;
    notebookUploadScripts?(input:{channelId:string;projectIds:string[];operationId:string}):Promise<NotebookUploadResult>;
    notebookUploadFiles?(input:{channelId:string;operationId:string}):Promise<NotebookUploadResult>;
    notebookListSources?(input:{channelId:string}):Promise<NotebookSourceList>;
    notebookReadSource?(input:{channelId:string;sourceRef:NotebookSourceRef}):Promise<NotebookSourceRead>;
    notebookSyncScripts?(input:NotebookSyncInput):Promise<NotebookSyncResult>;
    addStockSource?(mode:'local'|'streaming'):Promise<{configured:boolean}>;
    cacheStatus?():Promise<{files:number;bytes:number;quota:number}>;
    prepareProjectMedia?(projectId:string,assetId:string,range?:{start:number;end:number}):Promise<{bytes:number}>;
    releaseProjectMedia?(projectId:string):Promise<{files:number;bytes:number}>;
    checkAgent?(credentialId:string):Promise<{verified:boolean;tools:number;message:string}>;
    nativeAgentStatus?(client:AgentClient):Promise<{installed:boolean;authenticated:boolean;message:string;taskSupported?:boolean|null;desktopInstalled?:boolean|null;code?:string;source?:'manual'|'automatic'|null}>;
    antigravityStatus?():Promise<{installed:boolean;configured:boolean;credentialId?:string;message:string}>;
    configureAntigravity?(credentialId:string):Promise<{configured:boolean;message:string}>;
    chooseNativeAgent?(client:AgentClient):Promise<{selected:boolean}>;
    nativeAgentLogin?(client:AgentClient):Promise<{started:boolean}>;
    stopAgentRunner?():Promise<{enabled:false}>;
    configureAgentRunner?(credentialId:string,client:AgentClient,enabled:boolean):Promise<{enabled:boolean}>;
    prepareAgentConnection?(credentialId:string,client:AgentClient):Promise<{prepared:boolean}>;
    agentConnectionStatus?(client?:AgentClient):Promise<{client:AgentClient;credentialId:string;prepared:boolean;message:string}|null>;
    runAgentTask?(taskId:string):Promise<{enabled:boolean}>;
    agentRunnerStatus?():Promise<{enabled:boolean;busy:boolean;message:string;client?:AgentClient|null}>;
    managedStatus?(): Promise<ManagedStorageStatus>;
    configureStorage?(): Promise<{ configured: boolean }>;
    openStorage?(): Promise<void>;
    importToStorage?(category: 'clips' | 'musica' | 'sfx' | 'imagenes'): Promise<{ copied: number; duplicates: number; cancelled: boolean }>;
    cancelStorageImport?(): Promise<void>;
    openProjectStorage?(projectId: string): Promise<void>;
    bindWorkspace(workspaceId: string, accessToken: string): Promise<void>;
    clearWorkspace(): Promise<void>;
    listRoots(): Promise<import('@estudio/contracts').RootDescriptor[]>;
    revokeRoot(rootId: string): Promise<void>;
    listAssets(): Promise<{ id: string; rootId: string; relativePath: string; bytes: number; mime: string; present: boolean }[]>;
    connectWorker(token: string): Promise<void>;
    syncCatalog(): Promise<{ synchronized: number }>;
    workerStatus(): Promise<string>;
    configureTranscriber?(): Promise<{ configured: boolean }>;
    configureAcceleration?(): Promise<{ configured: boolean }>;
    saveAgentToken(credentialId: string, token: string): Promise<{ command: string; args: string[]; env: Record<string, string> }>;
    agentConfig(credentialId: string): Promise<{ command: string; args: string[]; env: Record<string, string> }>;
    installAgent?(credentialId:string,client:AgentClient):Promise<{client:string;server:string;configured:boolean}>;
  };
}

export interface DesktopUpdateState {
  currentVersion: string;
  phase: 'idle' | 'checking' | 'current' | 'available' | 'downloading' | 'ready' | 'error' | 'disabled';
  version?: string;
  percent?: number;
  checkedAt?: string;
  message?: string;
}

export interface ManagedStorageStatus {
  configured: boolean;
  rootId?: string;
  files?: number;
  bytes?: number;
  freeBytes?: number;
  importing?: { total: number; completed: number; file: string; bytes: number };
}

/**
 * DraftStore: borradores pendientes de subir. Una operación aceptada por el
 * servidor queda confirmada antes de retirar el pendiente.
 */
export interface PendingDraft {
  documentId: string;
  workspaceId: string;
  baseRevisionId: string | null;
  operationId: string;
  content: unknown;
  updatedAt: string;
  /** Estado conocido: pendiente, en conflicto o documento borrado en remoto. */
  state: 'pending' | 'conflict' | 'deleted_remote';
  conflictId?: string | null;
}

export interface DraftStore {
  get(documentId: string): Promise<PendingDraft | null>;
  put(draft: PendingDraft): Promise<void>;
  remove(documentId: string, operationId: string): Promise<void>;
  list(): Promise<PendingDraft[]>;
}

/** Retira el pendiente solo si sigue siendo la misma operación confirmada. */
export function createMemoryDraftStore(): DraftStore {
  const map = new Map<string, PendingDraft>();
  return {
    get: (id) => Promise.resolve(map.get(id) ?? null),
    put: (d) => {
      map.set(d.documentId, structuredClone(d));
      return Promise.resolve();
    },
    remove: (id, op) => {
      if (map.get(id)?.operationId === op) map.delete(id);
      return Promise.resolve();
    },
    list: () => Promise.resolve([...map.values()]),
  };
}

const DB_NAME = 'estudio-creador-drafts';
const STORE = 'drafts';

function openDb(factory: IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = factory.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: 'documentId' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(db: IDBDatabase, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const r = fn(t.objectStore(STORE));
    let result: T | undefined;
    if (r) r.onsuccess = () => (result = r.result);
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

/** Vista web: IndexedDB. Los borradores sobreviven a cerrar la pestaña. */
export function createIndexedDbDraftStore(factory: IDBFactory = globalThis.indexedDB): DraftStore {
  const dbp = openDb(factory);
  return {
    async get(id) {
      return ((await tx<PendingDraft>(await dbp, 'readonly', (s) => s.get(id))) as PendingDraft | undefined) ?? null;
    },
    async put(d) {
      await tx(await dbp, 'readwrite', (s) => void s.put(d));
    },
    async remove(id, op) {
      const db = await dbp;
      await new Promise<void>((resolve, reject) => {
        const t = db.transaction(STORE, 'readwrite');
        const store = t.objectStore(STORE);
        const g = store.get(id);
        g.onsuccess = () => {
          if ((g.result as PendingDraft | undefined)?.operationId === op) store.delete(id);
        };
        t.oncomplete = () => resolve();
        t.onerror = () => reject(t.error);
      });
    },
    async list() {
      return ((await tx<PendingDraft[]>(await dbp, 'readonly', (s) => s.getAll())) as PendingDraft[] | undefined) ?? [];
    },
  };
}
